import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeLinkedInCode } from '../lib/api/linkedin.js';

/**
 * OAuth redirect target: https://app.assayer-ai.com/linkedin/callback
 *
 * LinkedIn sends the operator's browser here with ?code&state (or ?error).
 * This SPA route already carries the operator's session, so it hands the
 * code to the backend's authenticated exchange endpoint — no unauthenticated
 * gateway route is needed. The code is single-use and lives 30 minutes.
 */
export function LinkedInCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState('Completing the LinkedIn connection…');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const code = params.get('code');
    const state = params.get('state');
    const err = params.get('error');
    if (err) {
      setFailed(true);
      setMessage(`LinkedIn declined: ${params.get('error_description') || err}`);
      return;
    }
    if (!code || !state) {
      setFailed(true);
      setMessage('Missing code or state on the callback.');
      return;
    }
    exchangeLinkedInCode(code, state)
      .then(() => navigate('/settings/linkedin', { replace: true }))
      .catch((e: any) => {
        setFailed(true);
        setMessage(e?.response?.data?.detail || e?.message || 'The code exchange failed.');
      });
  }, [params, navigate]);

  return (
    <div className="p-6 max-w-xl">
      <h1 className="text-xl font-semibold mb-2">LinkedIn connection</h1>
      <p className={failed ? 'text-red-700' : 'text-slate-600'}>{message}</p>
      {failed && (
        <button
          type="button"
          className="mt-4 rounded border px-3 py-1.5 text-sm"
          onClick={() => navigate('/settings/linkedin')}
        >
          Back to LinkedIn settings
        </button>
      )}
    </div>
  );
}
